/*
 * What a conversation *is*, and the things a person does to it rather than in it: the
 * `chat.conversation` view of the details column (UX §2.1, §8 #3).
 *
 * It used to be two buttons in the header (`ConversationActions.tsx`). They are here because this is
 * where their answer already is: "Add people" is read next to the people who are in, and "Leave" next
 * to what one would be leaving. The column reads top to bottom as the three questions it answers:
 * what this conversation is (its own text, then one line of fact), who is in it, and what one does to
 * it. The actions are one row above a rule, and leaving is the quiet danger rank — the filled red
 * belongs to the confirmation, which is where the decision is actually made (VISUAL.md §7.7, G-78).
 *
 * There is no "About" heading and no "Description" heading: the tab strip over the column already
 * says About, and three uppercase micro-headings over ninety words said nothing the words themselves
 * did not (D-06 S8).
 *
 * The column is also where a channel is written rather than only read (X-A4 N14). `PATCH
 * /api/chat/conversations/{id}` has taken a name, a short name, a topic and a purpose since the
 * spine was built and nothing ever called it; the topic and the description are edited where they
 * stand, and the two names — which are the address, and therefore break links — behind a dialog
 * that says so.
 *
 * Only a channel or a private conversation offers all of that. A direct conversation cannot be
 * left, renamed or added to — it is the pair, not a room — and adding somebody to one is a promotion
 * into a group, which is a different decision and a different route (`…/promote`, spec 05 §2.5).
 *
 * Under the facts stands the room's switch for AI chats (`agents.tsx`): whether Claude may read it in
 * AI chats whose owners could all read it. A direct or group conversation keeps that switch in its ⋯
 * menu instead.
 *
 * A room is managed by its members and by an administrator who is not in it (the matrix's `MN, or AU
 * without M`): the administrator's outside view (`AdminOutside`) renders this very column, its
 * conversation read through `conversationMeta`, and every write here goes through `manage.ts`, which
 * asks an administrator for the password and re-reads an outside view after the write. A channel
 * also shows the groups it belongs to (`ChannelGroups`), between its facts and its members.
 *
 * An archived conversation refuses every write, and the refusal is made here rather than fetched:
 * the service does answer a denial with its reason (`writableConversation`), but a reason that
 * arrives after a round trip is one nobody connects to what they just did, so the controls are not
 * offered and the block says why in one line.
 *
 * Adding asks one question the route cannot answer for the caller: whether the people arriving may
 * read what was said before they did. The default is the conversation's own window — what a channel
 * is for — and "from now on" is there for the case where it is not.
 *
 * Two things here are an administrator's and reach past the chat module, because the facts they are
 * about are the instance's rather than this conversation's (U1, U16):
 *
 *  - *Everyone joins* makes this channel the instance's default channel (`chat.default_channel`).
 *    The setting is an instance one, so its value never travels in the model — an administrator's
 *    column reads it from `GET /api/admin/settings` and writes it back under the version it read,
 *    which is why a second administrator's change is a refusal to read and not a silent overwrite.
 *  - *Invite by e-mail* makes the account that is not there yet. A team of seven invites from where
 *    it noticed the gap, which is this dialog and not the administration area (D-10 A5).
 *
 * Both go through `core/admin/api.ts`, which is where the `admin.*` routes are typed; the details
 * column is a caller of them and owns none of them.
 */

import {
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
	type KeyboardEvent,
	type ReactNode,
	type Ref,
} from "react"
import { adminApi, type DefaultChannel } from "../../../core/admin/api.ts"
import { useStepUpField, withStepUp } from "../../../core/auth/index.ts"
import { PeoplePicker, pickableFor } from "../../../core/groups/index.ts"
import { formatDate, isoDateTime } from "../../../core/i18n/format.ts"
import { t as coreT } from "../../../core/i18n/index.ts"
import { Avatar } from "../../../core/people/Avatar.tsx"
import { directMessagePath } from "../../../core/people/navigation.ts"
import { AccountPill } from "../../../core/people/PersonChip.tsx"
import { StatusMark } from "../../../core/people/PersonStatus.tsx"
import { withAway } from "../../../core/people/presence.ts"
import { ProfileView } from "../../../core/people/ProfileView.tsx"
import { isAdmin } from "../../../core/people/standing.ts"
import { statusOf } from "../../../core/people/status.ts"
import { Icon, useDetails } from "../../../core/shell/index.ts"
import type { ConversationId, GroupId, GroupView, UserId } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import {
	Button,
	Dialog,
	emojiText,
	Field,
	FormAlert,
	Menu,
	type ButtonSize,
	type MenuItem,
} from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import { chatSlice, conversationMeta } from "../state/selectors.ts"
import type { ConversationView } from "../wire/views.ts"
import { AgentsBox } from "./agents.tsx"
import { leaveConversation, PURPOSE_MAX, TOPIC_MAX, type HistoryMode } from "./api.ts"
import { ChannelGroups } from "./ChannelGroups.tsx"
import "./conversation.css"
import { addMembers, removeMember, updateConversation } from "./manage.ts"
import { goTo } from "./opening.ts"
import { channelKindLabel, rejoinableAfterLeaving } from "./owners.ts"
import { RenameDialog } from "./RenameDialog.tsx"

/** Where leaving lands: the start page, because the conversation just stopped being a place. */
const AFTER_LEAVING = "/"

/**
 * How many member rows a 256 px column shows before it says how many there are. Eight is what fits
 * above the footer without the column becoming a scroller of its own (VISUAL.md §7.7).
 */
const MEMBERS_SHOWN = 8

/**
 * What the people arriving may read, as the two cards the dialog offers. The route cannot answer this
 * for the caller: the default is the conversation's own window — what a channel is for — and "from
 * now on" is there for the case where it is not.
 */
const HISTORY_MODES = [
	{ value: "full", key: "chat.members.historyFull" },
	{ value: "fromNow", key: "chat.members.historyFromNow" },
] as const satisfies { value: HistoryMode; key: string }[]

/**
 * What the About block calls this kind of conversation. A room is named by `channelKindLabel`,
 * because a channel's name for itself says which groups it belongs to: "Channel for Team members".
 */
function kindLabel(meta: ConversationView, groups: Readonly<Record<GroupId, GroupView>>): string {
	switch (meta.kind) {
		case "channel":
		case "private":
			return channelKindLabel(meta.kind, meta.ownerGroupIds, groups)
		case "dm":
			return t("chat.header.kindDirect")
		case "groupDm":
			return t("chat.header.kindGroupDirect")
	}
}

/**
 * Whether this reader may rewrite this conversation's own text and change who is in it. It is the
 * matrix cell of `chat.conversation.update` and `chat.member.add`/`.remove` — an active member of a
 * room, or an administrator without being in it — and the archive rule the service applies inside
 * its own transaction; the two together are what the interface offers, in the header band and in
 * this column alike.
 */
export function canEditConversation(
	meta: ConversationView,
	isMember: boolean,
	readerIsAdmin: boolean,
): boolean {
	const isRoom = meta.kind === "channel" || meta.kind === "private"
	return (isMember || readerIsAdmin) && isRoom && meta.archivedAt === undefined
}

/**
 * The registered view. The details column hands every view the request's parameters as strings or
 * numbers, so the id is read out of them here and the rest of the file is ordinary props.
 */
export function ConversationDetailsView({ params }: { params: Record<string, string | number> }) {
	return <ConversationDetails conv={Number(params["conv"])} />
}

export function ConversationDetails({ conv }: { conv: ConversationId }) {
	const meta = useModel((model) => conversationMeta(model, conv))
	const meId = useModel((model) => model.me?.id ?? 0)
	const isMember = useModel((model) => chatSlice(model).self[conv]?.state === "active")
	const readerIsAdmin = useModel((model) => isAdmin(model.me))
	const groups = useModel((model) => model.groups)
	const [adding, setAdding] = useState(false)
	const [leaving, setLeaving] = useState(false)
	const [renaming, setRenaming] = useState(false)
	// Only an administrator may read the instance settings at all, and only a room is one a person is
	// put into, so nothing is asked of the server for anybody else's column or for a profile.
	const isRoomKind = meta?.kind === "channel" || meta?.kind === "private"
	const defaultChannel = useDefaultChannel(readerIsAdmin && isRoomKind)
	if (!meta) return <p className="tb-empty">{t("chat.conversation.notFound")}</p>

	// What a person opening the details of a direct conversation wants is the person: their local
	// time, their e-mail, the channels you share. "No topic yet." and "Created 8 September" are
	// facts about a room, and a direct conversation is not one (X-A1 D21).
	const other = otherPerson(meta, meId)
	if (other !== undefined) return <ProfileView params={{ userId: other }} />

	const isRoom = meta.kind === "channel" || meta.kind === "private"
	const canEdit = canEditConversation(meta, isMember, readerIsAdmin)
	return (
		<div className="tb-convdetails">
			{/* About carries no heading of its own: the tab strip above the column already says the word,
			    and three uppercase micro-headings over ninety words said nothing the words did not
			    (D-06 S8). What is left is the conversation's own text, then one quiet line of fact. */}
			<section>
				{canEdit ? (
					<InlineEdit
						label={t("chat.details.topic")}
						editTitle={t("chat.details.editTopic")}
						value={meta.topic ?? ""}
						emptyLabel={t("chat.header.topicAdd")}
						maxLength={TOPIC_MAX}
						className="tb-convdetails__topic"
						onSave={async (topic) => void (await updateConversation(conv, { topic }))}
					/>
				) : meta.topic === undefined || meta.topic === "" ? null : (
					<p className="tb-convdetails__topic">{emojiText(meta.topic)}</p>
				)}
				{!isRoom ? null : canEdit ? (
					<InlineEdit
						label={t("chat.details.description")}
						editTitle={t("chat.details.editDescription")}
						value={meta.purpose ?? ""}
						emptyLabel={t("chat.details.descriptionAdd")}
						maxLength={PURPOSE_MAX}
						multiline
						className="tb-convdetails__purpose"
						onSave={async (purpose) => void (await updateConversation(conv, { purpose }))}
					/>
				) : meta.purpose === undefined || meta.purpose === "" ? null : (
					<p className="tb-convdetails__purpose">{emojiText(meta.purpose)}</p>
				)}
				<KindAndCreated kind={kindLabel(meta, groups)} at={meta.createdAt} />
				{isRoom && isMember && meta.archivedAt !== undefined ? (
					<p className="tb-convdetails__fact">{t("chat.details.archivedNote")}</p>
				) : null}
				{/* Whether Claude may read the room in AI chats: a fact about the conversation, under the
				    facts, beside the instance's own question below. */}
				{isRoom ? <AgentsBox meta={meta} isMember={isMember} /> : null}
				{/* The instance's own question, asked where its answer is about: only a group channel can be
				    the one everybody in its groups joins, and only an administrator decides it. It stands
				    under the facts rather than between the topic and the description, which are the
				    channel's own text. */}
				{readerIsAdmin && meta.kind === "channel" && meta.archivedAt === undefined ? (
					<EveryoneJoins conv={conv} channel={defaultChannel} />
				) : null}
			</section>

			{meta.kind === "channel" ? <ChannelGroups conv={meta} isMember={isMember} /> : null}

			<Members
				conv={meta}
				meId={meId}
				canRemove={canEdit}
				{...(canEdit ? { onAdd: () => setAdding(true) } : {})}
			/>

			{isRoom && (isMember || canEdit) ? (
				// The row itself is the control layer's `.tb-actions`; the class beside it carries what
				// that row does not and no other action row wants (`conversation.css`). *Add people* is
				// not here: it is offered once, at the top of the member list, where the question it
				// answers already stands (X-B1 P3). An administrator outside renames and never leaves.
				<div className="tb-actions tb-convdetails__actions">
					{canEdit ? (
						<Button
							variant="neutral"
							size="sm"
							label={t("chat.details.rename")}
							onClick={() => setRenaming(true)}
						/>
					) : null}
					{isMember ? (
						<Button
							variant="danger-quiet"
							size="sm"
							label={t("chat.members.leaveHere")}
							onClick={() => setLeaving(true)}
						/>
					) : null}
				</div>
			) : null}

			{renaming ? <RenameDialog conv={meta} onClose={() => setRenaming(false)} /> : null}
			<AddPeopleDialog
				conv={meta}
				open={adding}
				onOpenChange={setAdding}
				defaultConv={defaultChannel.value?.conv ?? 0}
			/>
			<LeaveDialog conv={meta} open={leaving} onOpenChange={setLeaving} />
		</div>
	)
}

/** What the About block and the add dialog both know about the instance's default channel. */
interface DefaultChannelState {
	/** What the server says, or null while nothing has been read — no request, or one that failed. */
	value: DefaultChannel | null
	/** Names this channel as the default one, or clears it with 0. */
	set: (conv: number) => Promise<void>
	busy: boolean
	/** The refusal of the last write, as a locale key. */
	error: string | null
}

/**
 * The instance's default channel, read once for the column that is allowed to read it.
 *
 * The value is not in the model and cannot be: an instance setting is broadcast as a key and a
 * version, and the value is refetched by whoever may read it (protocol.md §2.5.2). A column that may
 * not — anybody but an administrator — asks for nothing and offers nothing, and a read that fails
 * leaves `value` at null, so the interface says nothing about a fact it does not have.
 *
 * The write carries the version the read answered with, which is what makes a second administrator's
 * change a refusal ("This setting changed in the meantime. Please reload.") instead of a silent
 * overwrite. After such a refusal the value is read again, so the box shows what is true now — that
 * is the opposite of a retry: the sentence stays on screen and the next press is a person's.
 */
function useDefaultChannel(enabled: boolean): DefaultChannelState {
	const [value, setValue] = useState<DefaultChannel | null>(null)
	const read = useAsyncAction(async () => {
		setValue(await adminApi.readDefaultChannel())
	})
	const reread = read.run

	useEffect(() => {
		if (enabled) void reread()
	}, [enabled, reread])

	const write = useAsyncAction(async (next: number) => {
		try {
			// The action needs a fresh password tier (`admin.settings.write` is AUH, security.md §9.3).
			// A checkbox stands alone, so the one mounted host asks for it — there is no form here whose
			// last field it could be, and no dialog over which a second one would open.
			setValue(await withStepUp(() => adminApi.writeDefaultChannel(next, value?.version ?? 0)))
		} catch (err) {
			void reread()
			throw err
		}
	})

	return { value, set: write.run, busy: write.busy, error: write.error }
}

/**
 * *Everyone joins* — this channel is the instance's default one: every account its owner groups
 * reach is in it, and such an account is put into it when it is created (U1, D-10 D2; a guest the
 * groups do not reach never is). It is a check box and not a switch,
 * because what it states is a fact about the instance rather than a preference of the reader.
 *
 * Nothing is drawn until the setting has been read: a box whose state is a guess would show "no" for
 * the channel that is the default one.
 */
function EveryoneJoins({ conv, channel }: { conv: ConversationId; channel: DefaultChannelState }) {
	const hintId = useId()
	if (channel.value === null) return null
	return (
		<div className="tb-field">
			<label className="tb-check">
				<input
					type="checkbox"
					checked={channel.value.conv === conv}
					disabled={channel.busy}
					aria-describedby={hintId}
					onChange={(event) => void channel.set(event.target.checked ? conv : 0)}
				/>
				<span className="tb-check__label">{t("chat.details.everyoneJoins")}</span>
			</label>
			<p className="tb-field__hint tb-check__hint" id={hintId}>
				{t("chat.details.everyoneJoinsHint")}
			</p>
			<FormAlert messageKey={channel.error} />
		</div>
	)
}

/**
 * The module's one in-place editor: the value is a button until somebody presses it, the box that
 * opens carries the server's own cap in characters, `Escape` and *Cancel* leave the value exactly as
 * it was, and a refusal stands under the box instead of passing by as a notice.
 *
 * It lives beside the fields it changes rather than in a file of its own, and the band above the
 * timeline renders the topic with it too (`ConversationHeader.tsx`): editing a conversation's own
 * text is one rule, and a second copy of it up there is how the two would drift apart.
 */
export function InlineEdit({
	label,
	editTitle,
	value,
	emptyLabel,
	emptyBefore,
	quiet = false,
	size,
	maxLength,
	multiline = false,
	className,
	onSave,
}: {
	/**
	 * Already translated: the field's name. It is the box's accessible name and nothing more — the
	 * box opens exactly where the value stood, under the heading that already names it, so a caption
	 * over it would be the same word twice (X-A3 V22).
	 */
	label: string
	/**
	 * Already translated: the closed button's tooltip while the field has a value. An empty field
	 * needs none — its own text ("Add a topic") already says what pressing it does.
	 */
	editTitle: string
	/** What stands there now; the empty string when nothing does. */
	value: string
	/** Already translated: what the button says while the field is empty — it names the action. */
	emptyLabel: string
	/**
	 * A glyph before that label, for the surface where the empty control has to read as an action
	 * rather than as a missing sentence: the band sets the pencil, where the words alone would look
	 * like a topic somebody had written (VISUAL.md §7.3).
	 */
	emptyBefore?: ReactNode
	/** The closed control rests muted and comes up under the pointer: a glyph button in a dense row. */
	quiet?: boolean
	/** The closed control's rung; the band is the dense one. */
	size?: ButtonSize
	/** The server's own cap, in characters (`internal/modules/chat/model.go`). */
	maxLength: number
	/** A description is a paragraph and a topic is a line: only the paragraph gets a textarea. */
	multiline?: boolean
	/** The closed button's class, so each surface keeps the type it draws the value in. */
	className?: string
	onSave: (next: string) => Promise<void>
}) {
	const [editing, setEditing] = useState(false)
	const [draft, setDraft] = useState(value)
	const control = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

	// The box opens with the caret in it: a field that has to be clicked a second time before it can
	// be typed in is a field that was not really opened.
	useEffect(() => {
		if (editing) control.current?.focus()
	}, [editing])

	const save = useAsyncAction(async () => {
		await onSave(draft.trim())
		setEditing(false)
	})

	if (!editing) {
		// A written value goes through `emojiText`, because the interface face carries no colour glyphs
		// and a topic with a 🎉 in it came out of a label as a tofu box (D-01 §4.9) — which is why the
		// value travels in the leading slot as a node rather than as the button's plain `label`.
		//
		// A control whose content is the value has the value as its accessible name, so a screen reader
		// heard the topic and no verb (X-B3 P7): the name says what pressing it does *and* what it
		// would edit ("Edit the topic: Everything that gets looked at"), while the tooltip stays the
		// action alone — a sighted reader can see the sentence it is over.
		return (
			<Button
				variant="ghost"
				{...(quiet ? { quiet } : {})}
				{...(size === undefined ? {} : { size })}
				{...(className === undefined ? {} : { className })}
				{...(value === ""
					? { label: emptyLabel, ...(emptyBefore === undefined ? {} : { before: emptyBefore }) }
					: {
							before: emojiText(value),
							title: editTitle,
							"aria-label": t("chat.details.editValue", { action: editTitle, value }),
						})}
				onClick={() => {
					setDraft(value)
					save.reset()
					setEditing(true)
				}}
			/>
		)
	}

	/**
	 * `Escape` closes the field and nothing else. The details column answers `Escape` too (UX §2.2)
	 * with a listener on the region, and a reader who presses it while a box is open means the box —
	 * so this claims the keystroke in the **capture** phase, which is the only phase that runs before
	 * that listener: React dispatches both from the root container, and the column's own is a native
	 * bubble listener on an element in between. `preventDefault` is what the column reads as
	 * "somebody inside has answered this already" (`Shell.tsx`); stopping it as well keeps it from
	 * travelling any further.
	 */
	function cancelOnEscape(event: KeyboardEvent<HTMLElement>) {
		if (event.key !== "Escape") return
		event.preventDefault()
		event.stopPropagation()
		setEditing(false)
	}

	return (
		<form
			className="tb-inline-edit"
			onSubmit={(event) => {
				event.preventDefault()
				void save.run()
			}}
		>
			{multiline ? (
				<textarea
					ref={control as Ref<HTMLTextAreaElement>}
					className="tb-field__control"
					aria-label={label}
					rows={4}
					value={draft}
					maxLength={maxLength}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDownCapture={cancelOnEscape}
				/>
			) : (
				<input
					ref={control as Ref<HTMLInputElement>}
					className="tb-field__control"
					aria-label={label}
					value={draft}
					maxLength={maxLength}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDownCapture={cancelOnEscape}
				/>
			)}
			<div className="tb-inline-edit__actions">
				<Button
					variant="neutral"
					label={coreT("action.cancel")}
					onClick={() => setEditing(false)}
				/>
				<Button variant="primary" type="submit" label={coreT("action.save")} disabled={save.busy} />
			</div>
			<FormAlert messageKey={save.error} />
		</form>
	)
}

/**
 * The one other person in a direct conversation, or undefined for a group and a room. A group is
 * not one person and keeps the member list; a conversation of one — one's own notes — has nobody
 * else's profile to show.
 */
function otherPerson(conv: ConversationView, meId: UserId): UserId | undefined {
	if (conv.kind !== "dm") return undefined
	const others = (conv.memberIds ?? []).filter((id) => id !== meId)
	return others.length === 1 ? others[0] : undefined
}

/**
 * Who is in. A row opens that person's profile in the same column, which is the shortest way from
 * "who is this" to the answer, and its menu holds what one does *with* a person from here: their
 * profile, a direct conversation, and — on anybody but oneself — taking them out of the room. After
 * the name stands the glyph of whatever status that person published, which is the other half of the
 * question this block is opened with: who is in, and which of them is around.
 * Leaving is the Leave button and never a menu row, because the two are different decisions and the
 * stream tells them apart. Above the inline limit the wire sends no ids at all (protocol.md §2.6)
 * and the block says so rather than showing an empty list.
 */
function Members({
	conv,
	meId,
	canRemove,
	onAdd,
}: {
	conv: ConversationView
	meId: UserId
	canRemove: boolean
	/** Present where this reader may add somebody: the list's first row is then the way to do it. */
	onAdd?: () => void
}) {
	const users = useModel((model) => model.users)
	// The whole presence picture rather than a hook per row: the slice holds one map and a row is a
	// lookup in it, so a list of eleven people costs one subscription (`presence.ts`).
	const presence = useModel((model) => chatSlice(model).presence)
	const details = useDetails()
	const [removing, setRemoving] = useState<UserId | null>(null)
	const [showAll, setShowAll] = useState(false)
	const ids = conv.memberIds ?? []
	// Eight is what a 256 px column shows without becoming a scroller of its own, and a room of
	// eleven is then eight faces and the sentence that says so.
	const shown = showAll ? ids : ids.slice(0, MEMBERS_SHOWN)
	return (
		<section>
			<h3>{t("chat.conversation.members", { count: conv.memberCount })}</h3>
			{ids.length === 0 ? (
				<p className="tb-convdetails__fact">{t("chat.header.membersHidden")}</p>
			) : (
				<ul className="tb-convdetails__members">
					{onAdd === undefined ? null : (
						// First, not last: the reason a reader opens a member list is often to make it longer,
						// and the offer belongs where the eye already is (VISUAL.md §7.7).
						<li className="tb-convdetails__row">
							<Button
								variant="ghost"
								className="tb-convdetails__member tb-convdetails__add"
								before={<Icon name="userPlus" />}
								label={t("chat.members.add")}
								onClick={onAdd}
							/>
						</li>
					)}
					{shown.map((id) => {
						const user = users[id]
						const name = user?.displayName ?? ""
						// What this person published about themselves, for the glyph after the name and for
						// the dot: an away claim outranks whatever the transport observed (`presence.ts`).
						const status = statusOf(user)
						const seen = presence[id]
						const here = withAway(
							seen === "online" || seen === "away" ? seen : undefined,
							user?.statusAway,
						)
						const openProfile = () => details.open({ view: "core.profile", params: { userId: id } })
						const items: MenuItem[] = [
							{
								id: "profile",
								label: t("chat.members.viewProfile"),
								icon: "people",
								onSelect: openProfile,
							},
						]
						if (id !== meId && user !== undefined) {
							items.push({
								id: "message",
								label: t("chat.members.message"),
								icon: "reply",
								onSelect: () => goTo(directMessagePath(user.handle)),
							})
						}
						if (canRemove && id !== meId) {
							items.push({
								id: "remove",
								label: t("chat.members.remove"),
								icon: "userMinus",
								danger: true,
								separatorBefore: true,
								onSelect: () => setRemoving(id),
							})
						}
						return (
							<li className="tb-convdetails__row" key={id}>
								<Button
									variant="ghost"
									className="tb-convdetails__member"
									before={
										<Avatar
											size="sm"
											name={name}
											id={id}
											url={user?.avatarUrl}
											{...(here === undefined ? {} : { presence: here })}
										/>
									}
									label={name}
									// The glyph alone: a 256 px column has no room for the words, and they are
									// this mark's accessible name and its tooltip (`PersonStatus.tsx`).
									after={
										<>
											<AccountPill user={user} />
											<StatusMark status={status} />
										</>
									}
									onClick={openProfile}
								/>
								<Menu
									trigger={
										<Button
											variant="ghost"
											quiet
											icon
											size="sm"
											className="tb-convdetails__rowmenu"
											aria-label={t("chat.members.rowMenu", { name })}
											before={<Icon name="more" size={16} />}
										/>
									}
									items={items}
								/>
							</li>
						)
					})}
				</ul>
			)}
			{showAll || ids.length <= MEMBERS_SHOWN ? null : (
				<Button
					variant="ghost"
					quiet
					size="sm"
					label={t("chat.members.showAll", { count: ids.length })}
					onClick={() => setShowAll(true)}
				/>
			)}
			<RemoveMemberDialog
				conv={conv}
				user={removing}
				name={removing === null ? "" : (users[removing]?.displayName ?? "")}
				onClose={() => setRemoving(null)}
			/>
		</section>
	)
}

/**
 * Taking somebody out of a room is the one thing in this column a person does to *another* person,
 * so it is confirmed — and the confirmation says what stays behind: everything they wrote
 * (spec 05 §2.4).
 */
function RemoveMemberDialog({
	conv,
	user,
	name,
	onClose,
}: {
	conv: ConversationView
	user: UserId | null
	name: string
	onClose: () => void
}) {
	const remove = useAsyncAction(async (id: UserId) => {
		await removeMember(conv.id, id)
		onClose()
	})

	return (
		<Dialog
			open={user !== null}
			onOpenChange={(next) => {
				if (!next) {
					onClose()
					remove.reset()
				}
			}}
			title={t("chat.members.removeTitle", { name })}
			description={t("chat.members.removeBody")}
			footer={
				<div className="tb-dialog__actions">
					<Button variant="neutral" label={coreT("action.cancel")} onClick={onClose} />
					<Button
						variant="danger"
						label={t("chat.members.remove")}
						disabled={remove.busy}
						onClick={() => {
							if (user !== null) void remove.run(user)
						}}
					/>
				</div>
			}
		>
			<FormAlert messageKey={remove.error} />
		</Dialog>
	)
}

function AddPeopleDialog({
	conv,
	open,
	onOpenChange,
	defaultConv,
}: {
	conv: ConversationView
	open: boolean
	onOpenChange: (open: boolean) => void
	/** The instance's default channel, so an invited account lands there too; 0 when there is none. */
	defaultConv: number
}) {
	const readerIsAdmin = useModel((model) => isAdmin(model.me))
	const [selected, setSelected] = useState<UserId[]>([])
	const [history, setHistory] = useState<HistoryMode>("full")

	// A conversation above fifty members carries no inline member list (protocol.md §2.6); the
	// picker then offers everybody, and the server refuses the ones who are already in.
	const already = useMemo<UserId[]>(() => conv.memberIds ?? [], [conv.memberIds])
	/*
	 * Whether there is anybody left to add at all. With the whole team already in the channel the
	 * dialog said "Everybody is here already." and then kept the two history cards and a dead *Add
	 * people* under them — controls for a choice nobody can make (N5-R1-10). The sentence is then the
	 * whole dialog, *Close* is its one door, and an administrator keeps the invitation row, because
	 * somebody who is not here yet is exactly who is missing: the history cards stay for them and for
	 * nobody else.
	 */
	const nobodyToAdd = useModel((model) => pickableFor(model.users, already).length === 0)

	const add = useAsyncAction(async () => {
		await addMembers(conv.id, selected, history)
		setSelected([])
		onOpenChange(false)
	})

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				onOpenChange(next)
				if (!next) {
					setSelected([])
					add.reset()
				}
			}}
			title={t("chat.members.addTitle")}
			description={t("chat.members.addBody")}
			footer={
				<div className="tb-dialog__actions">
					<Button
						variant="neutral"
						label={coreT(nobodyToAdd ? "action.close" : "action.cancel")}
						onClick={() => onOpenChange(false)}
					/>
					{nobodyToAdd ? null : (
						<Button
							variant="primary"
							label={t("chat.members.add")}
							disabled={selected.length === 0 || add.busy}
							onClick={() => void add.run()}
						/>
					)}
				</div>
			}
		>
			<PeoplePicker
				exclude={already}
				selected={selected}
				onChange={setSelected}
				legend={t("chat.members.pick")}
			/>
			{/* Two answers as the product's choice cards: a short closed set is cards and never a
			    dropdown (§6.3), and each card is one sentence — the option and what it means — because a
			    card is one label and one target. They govern an invitation as well as an add, so they
			    stand while either is possible. */}
			{nobodyToAdd && !readerIsAdmin ? null : (
				<fieldset className="tb-choices">
					<legend className="tb-choices__legend">{t("chat.members.history")}</legend>
					{HISTORY_MODES.map((mode) => (
						<label className="tb-choice" key={mode.value}>
							<input
								type="radio"
								name="tb-add-history"
								checked={history === mode.value}
								onChange={() => setHistory(mode.value)}
							/>
							{t(mode.key)}
						</label>
					))}
				</fieldset>
			)}
			{readerIsAdmin ? (
				<InviteByEmail conv={conv.id} history={history} defaultConv={defaultConv} />
			) : null}
			<FormAlert messageKey={add.error} />
		</Dialog>
	)
}

/**
 * An address the invite row can act on at all: something on both sides of one `@` and a dot in the
 * domain. It is deliberately not the address law — the server owns that — only enough to tell "this
 * is a name I am searching for" from "this is somebody who is not here yet".
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The handle a new account gets from its address: the local part, folded to the handle law of
 * security.md §4.1 — lower case, its other characters dropped, never starting with a separator, at
 * most thirty-two. The server remains the authority: a local part that cannot become a handle is
 * refused with the sentence that says so, and the full form on `/admin/people` is where such an
 * account is made by hand.
 */
function handleFromEmail(email: string): string {
	return email
		.slice(0, email.indexOf("@"))
		.toLowerCase()
		.replace(/[^a-z0-9._-]/g, "")
		.replace(/^[._-]+/, "")
		.slice(0, 32)
}

/** The name a new account gets from its address: the handle's words, each capitalised. */
function nameFromEmail(email: string): string {
	return handleFromEmail(email)
		.split(/[._-]+/)
		.filter((word) => word !== "")
		.map((word) => word[0]!.toUpperCase() + word.slice(1))
		.join(" ")
}

/**
 * *Invite by e-mail* — the row an administrator reaches when the person they are looking for has no
 * account yet (U16, D-10 A5). One press creates the account, puts it into this conversation and into
 * the instance's default channel, and leaves the enrolment link on screen; the name and the handle are
 * read off the address, because the screen that asks for all three already exists (`/admin/people`)
 * and this row is for the case where nobody wants to go there.
 *
 * *As a guest* makes the account a guest instead: somebody from outside the team who sees the
 * conversations they are added to and the channels of their groups. A guest lands in this
 * conversation and **not** in the default channel — that channel belongs to the team, and putting a
 * guest there by a side effect of an invitation is exactly what the box was ticked to avoid.
 *
 * Creating an account needs a fresh password tier (`admin.account.create` is AUH, security.md §9.3)
 * and this row is inside a dialog, so the tier is asked for **in the row** with `useStepUpField` — a
 * second dialog over this one is exactly what D-10 D11 took out of the administration area.
 *
 * The link appears as soon as the account exists, before the two membership writes: a refusal from
 * either of them must not take away the one thing that cannot be asked for again.
 */
function InviteByEmail({
	conv,
	history,
	defaultConv,
}: {
	conv: ConversationId
	/** The history the dialog is about to grant, so the invited account arrives with the same window. */
	history: HistoryMode
	defaultConv: number
}) {
	const stepUp = useStepUpField()
	const [asking, setAsking] = useState(false)
	// The opener leaves the document when the box arrives, so the box takes the focus it had:
	// a disclosure that drops focus on the body is one a keyboard has to find its way back into.
	const box = useRef<HTMLInputElement>(null)
	const [typed, setTyped] = useState("")
	const [asGuest, setAsGuest] = useState(false)
	const [issued, setIssued] = useState<{ name: string; link: string } | null>(null)
	const [copied, setCopied] = useState(false)
	const email = typed.trim()

	useEffect(() => {
		if (asking) box.current?.focus()
	}, [asking])

	const invite = useAsyncAction(async () => {
		const created = await stepUp.submit(() =>
			adminApi.createAccount({
				email,
				handle: handleFromEmail(email),
				displayName: nameFromEmail(email),
				role: asGuest ? "guest" : "member",
			}),
		)
		setIssued({ name: created.user.displayName, link: created.invitationLink })
		await addMembers(conv, [created.user.id], history)
		// The default channel as well, unless this is it or the account is a guest's. The server puts
		// every account its groups reach into it the moment it is named; an account made afterwards is
		// put there here, because the hook that would do it at enrolment lives in `core/auth`.
		if (!asGuest && defaultConv !== 0 && defaultConv !== conv) {
			await adminApi.addToDefaultChannel(defaultConv, [created.user.id])
		}
	})

	async function copyLink(link: string) {
		try {
			await navigator.clipboard.writeText(link)
			setCopied(true)
		} catch {
			// A browser that refuses the clipboard leaves the link on screen, where it is selectable.
			setCopied(false)
		}
	}

	if (issued !== null) {
		return (
			<div className="tb-field">
				<p className="tb-field__hint">{t("chat.members.invited", { name: issued.name })}</p>
				<p className="tb-linkbox">{issued.link}</p>
				{/* The administration area's own words for this very link and for having copied it: one
				    copy of them, so the two surfaces that hand a link over cannot drift apart. */}
				<Button
					variant="secondary"
					size="sm"
					before={<Icon name="copy" size={16} />}
					label={coreT("admin.link.copy")}
					onClick={() => void copyLink(issued.link)}
				/>
				{copied ? (
					<p className="tb-status" role="status">
						{coreT("admin.link.copied")}
					</p>
				) : null}
				<FormAlert messageKey={invite.error} />
			</div>
		)
	}

	// One line until it is wanted: the dialog's own decisions — who is added and what they may read —
	// keep the height they had, and this is the offer under them rather than a form in front of them.
	if (!asking) {
		return (
			<Button
				variant="ghost"
				quiet
				size="sm"
				before={<Icon name="userPlus" size={16} />}
				label={t("chat.members.inviteOpen")}
				onClick={() => setAsking(true)}
			/>
		)
	}

	return (
		<div className="tb-field">
			{/* Its own box rather than the picker's filter: the filter disappears with the list when
			    everybody already is in, which in a team of seven is exactly when an account is missing. */}
			<Field label={t("chat.members.inviteLabel")} hint={t("chat.members.inviteLead")}>
				{(control) => (
					<input
						{...control}
						ref={box}
						type="email"
						value={typed}
						autoComplete="off"
						onChange={(event) => setTyped(event.target.value)}
					/>
				)}
			</Field>
			{EMAIL_SHAPE.test(email) ? (
				<>
					<label className="tb-check">
						<input
							type="checkbox"
							checked={asGuest}
							onChange={(event) => setAsGuest(event.target.checked)}
						/>
						<span className="tb-check__label">{t("chat.members.inviteAsGuest")}</span>
					</label>
					<Button
						variant="secondary"
						size="sm"
						before={<Icon name="userPlus" size={16} />}
						label={t("chat.members.invite", { email })}
						busy={invite.busy}
						onClick={() => void invite.run()}
					/>
					{stepUp.field}
					<FormAlert messageKey={invite.error} />
				</>
			) : null}
		</div>
	)
}

/**
 * "Channel for Team members · created 14 October 2026", with the date a `<time datetime>`: a reader
 * who wants the moment has it, and the sentence's words stay the catalogue's own.
 */
function KindAndCreated({ kind, at }: { kind: string; at: number }) {
	const [before, after = ""] = t("chat.details.kindAndCreated", { kind, date: DATE_SLOT }).split(
		DATE_SLOT,
	)
	return (
		<p className="tb-convdetails__fact">
			{before}
			<time dateTime={isoDateTime(at)}>{formatDate(at)}</time>
			{after}
		</p>
	)
}

/** Where the date stands in the sentence, a character no translation holds. */
const DATE_SLOT = "\u0000"

function LeaveDialog({
	conv,
	open,
	onOpenChange,
}: {
	conv: ConversationView
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const leave = useAsyncAction(async () => {
		await leaveConversation(conv.id)
		onOpenChange(false)
		goTo(AFTER_LEAVING)
	})
	const rejoinable = useModel((model) => rejoinableAfterLeaving(conv, model.groups, model.me))

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("chat.members.leaveTitle")}
			description={rejoinable ? t("chat.members.leaveBody") : t("chat.members.leavePrivateBody")}
			footer={
				<div className="tb-dialog__actions">
					<Button
						variant="neutral"
						label={coreT("action.cancel")}
						onClick={() => onOpenChange(false)}
					/>
					<Button
						variant="danger"
						label={t("chat.members.leave")}
						disabled={leave.busy}
						onClick={() => void leave.run()}
					/>
				</div>
			}
		>
			<FormAlert messageKey={leave.error} />
		</Dialog>
	)
}
